import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { Job } from "@/data/types";
import type { Opportunity, OpportunityStage } from "@/data/opportunityTypes";
import { formatDisplayReference, formatLeadReference } from "@/data/opportunityTypes";

export type ProjectJobsTab = "active" | "attention" | "blocked" | "ready" | "completed" | "all";
export type ProjectOpportunityTab = "all" | "open" | "follow_up" | "won" | "lost";
export type ProjectReadinessLane = "ready" | "almost" | "blocked" | "not-started" | "in-install" | "all";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface ProjectJobsPageQuery {
  tab: ProjectJobsTab;
  page: number;
  pageSize: number;
  search?: string;
  branch?: string;
  region?: string;
}

export interface ProjectJobsSummary {
  active: number;
  attention: number;
  blocked: number;
  ready: number;
  completed: number;
  all: number;
  atRisk: number;
  pctReady: number;
}

export interface ProjectOpportunitiesPageQuery {
  tab: ProjectOpportunityTab;
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  rep?: string;
  disposition?: string;
  dateRange?: string;
}

export interface ProjectOpportunitiesSummary {
  all: number;
  open: number;
  followUp: number;
  won: number;
  lost: number;
  aging14: number;
  closeRate: number;
  pipelineValue: number;
}

export interface ProjectReadinessPageQuery {
  lane: ProjectReadinessLane;
  page: number;
  pageSize: number;
  search?: string;
}

export interface ProjectReadinessSummary {
  ready: number;
  almost: number;
  blocked: number;
  notStarted: number;
  inInstall: number;
  total: number;
}

export interface ProjectBranchRegionOptions {
  branches: string[];
  regions: string[];
}

export interface ProjectReadinessRow {
  job: Job;
  lane: Exclude<ProjectReadinessLane, "all">;
  materialPct: number;
}

type ProjectJobUiRow = {
  organization_id?: string | null;
  id: string;
  job_number?: string | null;
  title?: string | null;
  current_stage?: string | null;
  permit_required?: boolean | null;
  material_readiness?: string | null;
  notes?: string | null;
  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  metadata?: Record<string, unknown> | null;
  source_meta?: Job["sourceMeta"] | null;
  customer_id?: string | null;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  customer_address_line1?: string | null;
  customer_address_line2?: string | null;
  customer_city?: string | null;
  customer_state?: string | null;
  customer_postal_code?: string | null;
  customer_name?: string | null;
  project_type?: string | null;
  branch?: string | null;
  region?: string | null;
  assigned_crew?: string | null;
  assigned_subcontractor?: string | null;
  assignment_type?: string | null;
  permit_status?: string | null;
  qa_submitted?: boolean | null;
  readiness_percent?: number | null;
  is_completed?: boolean | null;
  is_active?: boolean | null;
  is_blocked?: boolean | null;
  needs_attention?: boolean | null;
  is_ready?: boolean | null;
  is_at_risk?: boolean | null;
  readiness_lane?: ProjectReadinessRow["lane"] | null;
  material_pct?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type ProjectOpportunityUiRow = {
  opportunity_id?: string | null;
  source_kind?: string | null;
  organization_id?: string | null;
  customer_id?: string | null;
  quote_id?: string | null;
  lead_id?: string | null;
  display_reference?: string | null;
  primary_name?: string | null;
  secondary_name?: string | null;
  primary_phone?: string | null;
  secondary_phone?: string | null;
  primary_email?: string | null;
  secondary_email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  project_type?: string | null;
  lead_source?: string | null;
  lead_cost?: number | null;
  stage?: string | null;
  assigned_rep?: string | null;
  assigned_rep_name?: string | null;
  total_estimate?: number | null;
  linked_job_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  notes?: string | null;
  won_at?: string | null;
  lost_at?: string | null;
  lost_reason?: string | null;
  is_open?: boolean | null;
  is_follow_up?: boolean | null;
  is_won?: boolean | null;
  is_lost?: boolean | null;
  is_aging_14?: boolean | null;
  pipeline_value?: number | null;
};

const JOBS_COLUMNS = [
  "organization_id",
  "id",
  "job_number",
  "title",
  "current_stage",
  "permit_required",
  "material_readiness",
  "notes",
  "address_line1",
  "city",
  "state",
  "postal_code",
  "metadata",
  "source_meta",
  "customer_id",
  "customer_first_name",
  "customer_last_name",
  "customer_address_line1",
  "customer_address_line2",
  "customer_city",
  "customer_state",
  "customer_postal_code",
  "customer_name",
  "project_type",
  "branch",
  "region",
  "assigned_crew",
  "assigned_subcontractor",
  "assignment_type",
  "permit_status",
  "qa_submitted",
  "readiness_percent",
  "is_completed",
  "is_active",
  "is_blocked",
  "needs_attention",
  "is_ready",
  "is_at_risk",
  "created_at",
  "updated_at",
].join(", ");

const READINESS_COLUMNS = `${JOBS_COLUMNS}, readiness_lane, material_pct`;

const OPPORTUNITY_COLUMNS = [
  "opportunity_id",
  "source_kind",
  "organization_id",
  "customer_id",
  "quote_id",
  "lead_id",
  "display_reference",
  "primary_name",
  "secondary_name",
  "primary_phone",
  "secondary_phone",
  "primary_email",
  "secondary_email",
  "address",
  "city",
  "state",
  "zip",
  "project_type",
  "lead_source",
  "lead_cost",
  "stage",
  "assigned_rep",
  "assigned_rep_name",
  "total_estimate",
  "linked_job_id",
  "created_at",
  "updated_at",
  "notes",
  "won_at",
  "lost_at",
  "lost_reason",
  "is_open",
  "is_follow_up",
  "is_won",
  "is_lost",
  "is_aging_14",
  "pipeline_value",
].join(", ");

function clampPage(query: { page: number; pageSize: number }) {
  const page = Math.max(0, Number(query.page) || 0);
  const pageSize = Math.max(1, Math.min(Number(query.pageSize) || 25, 100));
  return { page, pageSize, from: page * pageSize, to: page * pageSize + pageSize - 1 };
}

function sanitizeLike(value: string): string {
  return value.replace(/[%_]/g, "").replace(/,/g, " ").trim();
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function toStage(value: unknown): OpportunityStage {
  const stage = String(value ?? "").trim();
  if (
    stage === "Lead" ||
    stage === "Quote Created" ||
    stage === "Appointment Scheduled" ||
    stage === "Appointment Completed" ||
    stage === "Follow-Up Needed" ||
    stage === "Closed Won" ||
    stage === "Closed Lost"
  ) {
    return stage;
  }
  return "Lead";
}

function firstNonEmpty(values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function mapJobRow(row: ProjectJobUiRow): Job {
  const m = row.metadata ?? {};
  const customerName = String(row.customer_name ?? "").trim() || "Unknown";
  return {
    id: row.id,
    organizationId: String(row.organization_id ?? "") || undefined,
    jobNumber: row.job_number ?? row.id,
    customerName,
    address: {
      street: String(row.address_line1 ?? row.customer_address_line1 ?? ""),
      city: String(row.city ?? row.customer_city ?? ""),
      state: String(row.state ?? row.customer_state ?? ""),
      zip: String(row.postal_code ?? row.customer_postal_code ?? ""),
    },
    projectType: (String(row.project_type ?? m.projectType ?? "Other") || "Other") as Job["projectType"],
    assignedCrew: String(row.assigned_crew ?? m.assignedCrew ?? ""),
    assignmentType: (String(row.assignment_type ?? m.assignmentType ?? "Unassigned") || "Unassigned") as Job["assignmentType"],
    scheduledInstallDate: String(m.scheduledInstallDate ?? ""),
    permitRequired: Boolean(row.permit_required),
    permitStatus: (String(row.permit_status ?? m.permitStatus ?? "Not Required") || "Not Required") as Job["permitStatus"],
    orderStatus: (String(m.orderStatus ?? "Not Ordered") || "Not Ordered") as Job["orderStatus"],
    materialsStatus: (String(m.materialsStatus ?? "Not Ready") || "Not Ready") as Job["materialsStatus"],
    dispatchStatus: (String(m.dispatchStatus ?? "Not Ready") || "Not Ready") as Job["dispatchStatus"],
    contractorAssignmentStatus: String(m.contractorAssignmentStatus ?? ""),
    currentStage: (String(row.current_stage ?? m.currentStage ?? "Sale Closed") || "Sale Closed") as Job["currentStage"],
    notes: String(row.notes ?? ""),
    description: String(m.description ?? row.title ?? ""),
    issueFlag: Boolean(m.issueFlag ?? row.is_blocked),
    scopeSummary: String(m.scopeSummary ?? ""),
    payoutAmount: Number(m.payoutAmount ?? 0),
    assignedSubcontractor: String(row.assigned_subcontractor ?? m.assignedSubcontractor ?? ""),
    targetInstallDate: String(m.targetInstallDate ?? ""),
    activityHistory: (Array.isArray(m.activityHistory) ? m.activityHistory : []) as Job["activityHistory"],
    photos: (Array.isArray(m.photos) ? m.photos : []) as string[],
    qaSubmitted: Boolean(row.qa_submitted ?? m.qaSubmitted),
    materialReadiness: (String(row.material_readiness ?? "Not Ordered") || "Not Ordered") as Job["materialReadiness"],
    materialReadinessNotes: String(m.materialReadinessNotes ?? ""),
    missingItemsList: String(m.missingItemsList ?? ""),
    procurementStatus: (String(m.procurementStatus ?? "Not Ordered") || "Not Ordered") as Job["procurementStatus"],
    warehouseStatus: (String(m.warehouseStatus ?? "Not Received") || "Not Received") as Job["warehouseStatus"],
    finalDocumentationComplete: Boolean(m.finalDocumentationComplete),
    customerSignoff: Boolean(m.customerSignoff),
    closeoutNotes: String(m.closeoutNotes ?? ""),
    closeoutStatus: (String(m.closeoutStatus ?? "Not Started") || "Not Started") as Job["closeoutStatus"],
    branch: String(row.branch ?? m.branch ?? ""),
    region: String(row.region ?? m.region ?? ""),
    salesRep: String(m.salesRep ?? ""),
    phone: String(m.phone ?? ""),
    email: String(m.email ?? ""),
    secondaryContactName: String(m.secondaryContactName ?? ""),
    secondaryContactPhone: String(m.secondaryContactPhone ?? ""),
    secondaryContactEmail: String(m.secondaryContactEmail ?? ""),
    timeSlot: m.timeSlot as Job["timeSlot"],
    scheduledShifts: m.scheduledShifts as Job["scheduledShifts"],
    permitDetail: m.permitDetail as Job["permitDetail"],
    orderDetail: m.orderDetail as Job["orderDetail"],
    materialsList: m.materialsList as Job["materialsList"],
    financials: m.financials as Job["financials"],
    marketing: m.marketing as Job["marketing"],
    rescissionStatus: (String(m.rescissionStatus ?? "Cleared for Production") || "Cleared for Production") as Job["rescissionStatus"],
    projectConfig: m.projectConfig as Job["projectConfig"],
    externalRef: m.externalRef as Job["externalRef"],
    origin: m.origin as Job["origin"],
    scheduledStartTime: m.scheduledStartTime as string | undefined,
    scheduledEndTime: m.scheduledEndTime as string | undefined,
    timezone: m.timezone as string | undefined,
    externalCalendarEventId: m.externalCalendarEventId as string | undefined,
    boundJobSpec: (m.boundJobSpec ?? m.bound_job_spec) as Job["boundJobSpec"],
    executionReady: m.executionReady as Job["executionReady"],
    contractDocument: m.contractDocument as Job["contractDocument"],
    changeOrders: m.changeOrders as Job["changeOrders"],
    scopeLocked: m.scopeLocked as boolean | undefined,
    opportunityId: m.opportunityId as string | undefined,
    appointmentResultId: m.appointmentResultId as string | undefined,
    intakeApproved: m.intakeApproved as boolean | undefined,
    approvedForOrdering: m.approvedForOrdering as boolean | undefined,
    holdStatus: m.holdStatus as string | undefined,
    holdReason: m.holdReason as string | undefined,
    planningStatus: m.planningStatus as Job["planningStatus"],
    orderType: m.orderType as Job["orderType"],
    selectedVendor: m.selectedVendor as string | undefined,
    selectedVendorType: m.selectedVendorType as string | undefined,
    schedulingStatus: m.schedulingStatus as Job["schedulingStatus"],
    installReadiness: m.installReadiness as Job["installReadiness"],
    jobMaterialStatus: m.jobMaterialStatus as Job["jobMaterialStatus"],
    installStatus: m.installStatus as Job["installStatus"],
    qaStatus: m.qaStatus as Job["qaStatus"],
    reviewSent: m.reviewSent as boolean | undefined,
    reviewCompleted: m.reviewCompleted as boolean | undefined,
    reviewSatisfied: (m.reviewSatisfied as boolean | null | undefined) ?? null,
    reviewSentAt: m.reviewSentAt as string | undefined,
    referralRequested: m.referralRequested as boolean | undefined,
    referralReceived: m.referralReceived as boolean | undefined,
    referralRequestedAt: m.referralRequestedAt as string | undefined,
    googleReviewUrl: m.googleReviewUrl as string | undefined,
    siteReadiness: m.siteReadiness as Job["siteReadiness"],
    buildReviewApproved: m.buildReviewApproved as boolean | undefined,
    buildReviewStatus: m.buildReviewStatus as Job["buildReviewStatus"],
    buildReviewChecklist: m.buildReviewChecklist as Job["buildReviewChecklist"],
    qaReviewerChecklist: m.qaReviewerChecklist as Job["qaReviewerChecklist"],
    sourceMeta: (row.source_meta ?? m.sourceMeta ?? m.source_meta) as Job["sourceMeta"],
    tenantId: m.tenantId as string | undefined,
    createdAt: String(row.created_at ?? new Date().toISOString()),
    updatedAt: String(row.updated_at ?? row.created_at ?? new Date().toISOString()),
  } as Job;
}

function mapOpportunityRow(row: ProjectOpportunityUiRow): Opportunity {
  const id = String(row.opportunity_id ?? row.quote_id ?? row.lead_id ?? "");
  const leadId = String(row.lead_id ?? "").trim();
  const quoteId = String(row.quote_id ?? "").trim();
  const rawDisplay = String(row.display_reference ?? "").trim();
  const displayReference = rawDisplay
    ? (isUuidLike(rawDisplay) && leadId ? formatLeadReference(leadId) : formatDisplayReference(rawDisplay))
    : (leadId ? formatLeadReference(leadId) : id);
  const assignedRep = firstNonEmpty([row.assigned_rep_name, row.assigned_rep]);
  const stage = toStage(row.stage);
  const createdAt = String(row.created_at ?? new Date().toISOString());
  const updatedAt = String(row.updated_at ?? createdAt);
  const primaryName = String(row.primary_name ?? "Unknown Customer");

  return {
    id,
    quoteId: quoteId || null,
    leadId: leadId || null,
    displayReference,
    primaryName,
    secondaryName: String(row.secondary_name ?? ""),
    primaryPhone: String(row.primary_phone ?? ""),
    secondaryPhone: String(row.secondary_phone ?? ""),
    primaryEmail: String(row.primary_email ?? ""),
    secondaryEmail: String(row.secondary_email ?? ""),
    address: String(row.address ?? ""),
    city: String(row.city ?? ""),
    state: String(row.state ?? ""),
    zip: String(row.zip ?? ""),
    projectType: (String(row.project_type ?? "Other") || "Other") as Opportunity["projectType"],
    leadSource: String(row.lead_source ?? ""),
    leadCost: Number(row.lead_cost ?? 0) || undefined,
    stage,
    stageHistory: [],
    assignedRep: assignedRep && !isUuidLike(assignedRep) ? assignedRep : String(row.assigned_rep ?? ""),
    ownershipHistory: [],
    activities: [],
    totalEstimate: Number(row.total_estimate ?? 0),
    linkedJobId: String(row.linked_job_id ?? "") || undefined,
    createdAt,
    updatedAt,
    notes: String(row.notes ?? ""),
    wonAt: row.won_at ?? undefined,
    lostAt: row.lost_at ?? undefined,
    lostReason: row.lost_reason ?? undefined,
    customerId: row.customer_id ?? undefined,
    organizationId: row.organization_id ?? undefined,
    customerName: primaryName,
    customerPhone: String(row.primary_phone ?? ""),
    customerEmail: String(row.primary_email ?? ""),
    customerAddress: [row.address, row.city, [row.state, row.zip].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    createdBy: assignedRep,
    status: stage,
    quoteName: "",
    quote_number: displayReference,
    convertedJobId: String(row.linked_job_id ?? "") || undefined,
    appointmentId: id,
    linkedQuoteId: quoteId || undefined,
  } as Opportunity;
}

function applyJobTab(query: any, tab: ProjectJobsTab) {
  if (tab === "active") return query.eq("is_active", true);
  if (tab === "attention") return query.eq("needs_attention", true);
  if (tab === "blocked") return query.eq("is_blocked", true);
  if (tab === "ready") return query.eq("is_ready", true);
  if (tab === "completed") return query.eq("is_completed", true);
  return query;
}

function applyJobFilters(query: any, filters: Pick<ProjectJobsPageQuery, "search" | "branch" | "region">) {
  let next = query;
  const branch = String(filters.branch ?? "all").trim();
  const region = String(filters.region ?? "all").trim();
  if (branch && branch !== "all") next = next.eq("branch", branch);
  if (region && region !== "all") next = next.eq("region", region);
  const search = sanitizeLike(String(filters.search ?? ""));
  if (search) {
    const term = `%${search}%`;
    next = next.or(`job_number.ilike.${term},customer_name.ilike.${term},project_type.ilike.${term},current_stage.ilike.${term}`);
  }
  return next;
}

function applyOpportunityTab(query: any, tab: ProjectOpportunityTab) {
  if (tab === "open") return query.eq("is_open", true);
  if (tab === "follow_up") return query.eq("is_follow_up", true);
  if (tab === "won") return query.eq("is_won", true);
  if (tab === "lost") return query.eq("is_lost", true);
  return query;
}

function applyDateRange(query: any, dateRange?: string) {
  const days = Number(dateRange);
  if (!Number.isFinite(days) || days <= 0) return query;
  const start = new Date(Date.now() - days * 86_400_000).toISOString();
  return query.gte("created_at", start);
}

function applyOpportunityFilters(query: any, filters: Omit<ProjectOpportunitiesPageQuery, "tab" | "page" | "pageSize">) {
  let next = query;
  const search = sanitizeLike(String(filters.search ?? ""));
  if (search) {
    const term = `%${search}%`;
    next = next.or(`primary_name.ilike.${term},display_reference.ilike.${term},assigned_rep_name.ilike.${term},project_type.ilike.${term}`);
  }
  const status = String(filters.status ?? "all").trim();
  if (status && status !== "all") next = next.eq("stage", status);
  const rep = String(filters.rep ?? "all").trim();
  if (rep && rep !== "all") {
    next = isUuidLike(rep) ? next.eq("assigned_rep", rep) : next.ilike("assigned_rep_name", rep);
  }
  const disposition = String(filters.disposition ?? "all").trim();
  if (disposition && disposition !== "all") next = next.eq("lost_reason", disposition);
  return applyDateRange(next, filters.dateRange);
}

function applyReadinessFilters(query: any, filters: Pick<ProjectReadinessPageQuery, "lane" | "search">) {
  let next = query;
  if (filters.lane !== "all") next = next.eq("readiness_lane", filters.lane);
  const search = sanitizeLike(String(filters.search ?? ""));
  if (search) {
    const term = `%${search}%`;
    next = next.or(`job_number.ilike.${term},customer_name.ilike.${term},project_type.ilike.${term},city.ilike.${term},assigned_crew.ilike.${term}`);
  }
  return next;
}

async function countJobs(tab: ProjectJobsTab, filters: Pick<ProjectJobsPageQuery, "branch" | "region">): Promise<number> {
  const orgId = await getCurrentOrganizationId();
  let request = supabase.from("v_project_jobs_ui").select("id", { count: "exact", head: true }).eq("organization_id", orgId);
  request = applyJobTab(request, tab);
  request = applyJobFilters(request, filters);
  const { count, error } = await request;
  if (error) throw error;
  return count ?? 0;
}

export const ProjectPageService = {
  async getJobsPage(query: ProjectJobsPageQuery): Promise<PageResponse<Job>> {
    const { from, to } = clampPage(query);
    try {
      const orgId = await getCurrentOrganizationId();
      let request = supabase
        .from("v_project_jobs_ui")
        .select(JOBS_COLUMNS, { count: "exact" })
        .eq("organization_id", orgId)
        .order("sort_at", { ascending: false, nullsFirst: false })
        .range(from, to);
      request = applyJobTab(request, query.tab);
      request = applyJobFilters(request, query);
      const { data, error, count } = await request;
      if (error) throw error;
      return { data: ((data ?? []) as ProjectJobUiRow[]).map(mapJobRow), count: count ?? 0, error: null };
    } catch (error) {
      console.warn("ProjectPageService.getJobsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getJobsSummary(filters: Pick<ProjectJobsPageQuery, "branch" | "region"> = {}): Promise<{ data: ProjectJobsSummary | null; error: unknown | null }> {
    try {
      const branch = String(filters.branch ?? "all").trim();
      const region = String(filters.region ?? "all").trim();
      if ((!branch || branch === "all") && (!region || region === "all")) {
        const orgId = await getCurrentOrganizationId();
        const { data, error } = await supabase
          .from("v_project_jobs_summary")
          .select("active, attention, blocked, ready, completed, all_count, at_risk, pct_ready")
          .eq("organization_id", orgId)
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (data) {
          const row = data as Record<string, unknown>;
          return {
            data: {
              active: Number(row.active ?? 0),
              attention: Number(row.attention ?? 0),
              blocked: Number(row.blocked ?? 0),
              ready: Number(row.ready ?? 0),
              completed: Number(row.completed ?? 0),
              all: Number(row.all_count ?? 0),
              atRisk: Number(row.at_risk ?? 0),
              pctReady: Number(row.pct_ready ?? 0),
            },
            error: null,
          };
        }
      }
      const [active, attention, blocked, ready, completed, all, atRisk] = await Promise.all([
        countJobs("active", filters),
        countJobs("attention", filters),
        countJobs("blocked", filters),
        countJobs("ready", filters),
        countJobs("completed", filters),
        countJobs("all", filters),
        (async () => {
          const orgId = await getCurrentOrganizationId();
          let request = supabase.from("v_project_jobs_ui").select("id", { count: "exact", head: true }).eq("organization_id", orgId).eq("is_at_risk", true);
          request = applyJobFilters(request, filters);
          const { count, error } = await request;
          if (error) throw error;
          return count ?? 0;
        })(),
      ]);
      return {
        data: { active, attention, blocked, ready, completed, all, atRisk, pctReady: active > 0 ? Math.round((ready / active) * 100) : 0 },
        error: null,
      };
    } catch (error) {
      console.warn("ProjectPageService.getJobsSummary failed:", error);
      return { data: null, error };
    }
  },

  async getJobBranchRegionOptions(): Promise<{ data: ProjectBranchRegionOptions | null; error: unknown | null }> {
    try {
      const orgId = await getCurrentOrganizationId();
      const { data, error } = await supabase
        .from("v_project_jobs_ui")
        .select("branch, region")
        .eq("organization_id", orgId)
        .limit(5000);
      if (error) throw error;
      const branches = new Set<string>();
      const regions = new Set<string>();
      for (const row of (data ?? []) as Array<{ branch?: string | null; region?: string | null }>) {
        const branch = String(row.branch ?? "").trim();
        const region = String(row.region ?? "").trim();
        if (branch) branches.add(branch);
        if (region) regions.add(region);
      }
      return {
        data: {
          branches: Array.from(branches).sort(),
          regions: Array.from(regions).sort(),
        },
        error: null,
      };
    } catch (error) {
      console.warn("ProjectPageService.getJobBranchRegionOptions failed:", error);
      return { data: null, error };
    }
  },

  async getOpportunitiesPage(query: ProjectOpportunitiesPageQuery): Promise<PageResponse<Opportunity>> {
    const { from, to } = clampPage(query);
    try {
      const orgId = await getCurrentOrganizationId();
      let request = supabase
        .from("v_project_opportunities_ui")
        .select(OPPORTUNITY_COLUMNS, { count: "exact" })
        .eq("organization_id", orgId)
        .order("sort_at", { ascending: false, nullsFirst: false })
        .range(from, to);
      request = applyOpportunityTab(request, query.tab);
      request = applyOpportunityFilters(request, query);
      const { data, error, count } = await request;
      if (error) throw error;
      return { data: ((data ?? []) as ProjectOpportunityUiRow[]).map(mapOpportunityRow), count: count ?? 0, error: null };
    } catch (error) {
      console.warn("ProjectPageService.getOpportunitiesPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getOpportunitiesSummary(filters: Pick<ProjectOpportunitiesPageQuery, "rep"> = {}): Promise<{ data: ProjectOpportunitiesSummary | null; error: unknown | null }> {
    try {
      const orgId = await getCurrentOrganizationId();
      const rep = String(filters.rep ?? "all").trim();
      let request = supabase
        .from("v_project_opportunities_summary")
        .select("all_count, open_count, follow_up_count, won_count, lost_count, aging_14_count, close_rate, pipeline_value")
        .eq("organization_id", orgId)
        .limit(1);
      if (rep && rep !== "all") {
        request = isUuidLike(rep)
          ? request.eq("summary_scope", "rep").eq("assigned_rep", rep)
          : request.eq("summary_scope", "rep").ilike("assigned_rep_name", rep);
      } else {
        request = request.eq("summary_scope", "all");
      }
      const { data, error } = await request.maybeSingle();
      if (error) throw error;
      const row = (data ?? {}) as Record<string, unknown>;
      return {
        data: {
          all: Number(row.all_count ?? 0),
          open: Number(row.open_count ?? 0),
          followUp: Number(row.follow_up_count ?? 0),
          won: Number(row.won_count ?? 0),
          lost: Number(row.lost_count ?? 0),
          aging14: Number(row.aging_14_count ?? 0),
          closeRate: Number(row.close_rate ?? 0),
          pipelineValue: Number(row.pipeline_value ?? 0),
        },
        error: null,
      };
    } catch (error) {
      console.warn("ProjectPageService.getOpportunitiesSummary failed:", error);
      return { data: null, error };
    }
  },

  async getReadinessPage(query: ProjectReadinessPageQuery): Promise<PageResponse<ProjectReadinessRow>> {
    const { from, to } = clampPage(query);
    try {
      const orgId = await getCurrentOrganizationId();
      let request = supabase
        .from("v_project_install_readiness_ui")
        .select(READINESS_COLUMNS, { count: "exact" })
        .eq("organization_id", orgId)
        .order("sort_at", { ascending: false, nullsFirst: false })
        .range(from, to);
      request = applyReadinessFilters(request, query);
      const { data, error, count } = await request;
      if (error) throw error;
      return {
        data: ((data ?? []) as ProjectJobUiRow[]).map((row) => ({
          job: mapJobRow(row),
          lane: (row.readiness_lane ?? "blocked") as ProjectReadinessRow["lane"],
          materialPct: Number(row.material_pct ?? 0),
        })),
        count: count ?? 0,
        error: null,
      };
    } catch (error) {
      console.warn("ProjectPageService.getReadinessPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getReadinessSummary(): Promise<{ data: ProjectReadinessSummary | null; error: unknown | null }> {
    try {
      const { data, error } = await supabase
        .from("v_project_install_readiness_summary")
        .select("ready, almost, blocked, not_started, in_install, total")
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const row = (data ?? {}) as Record<string, unknown>;
      return {
        data: {
          ready: Number(row.ready ?? 0),
          almost: Number(row.almost ?? 0),
          blocked: Number(row.blocked ?? 0),
          notStarted: Number(row.not_started ?? 0),
          inInstall: Number(row.in_install ?? 0),
          total: Number(row.total ?? 0),
        },
        error: null,
      };
    } catch (error) {
      console.warn("ProjectPageService.getReadinessSummary failed:", error);
      return { data: null, error };
    }
  },
};
